/**
 * Hero section animations and interactive effects
 * This utility provides animation functions for the hero section
 */

// Animate typing effect for hero subtitle
export function initTypewriterEffect(element, phrases, options = {}) {
  if (!element) return;
  
  const defaultOptions = {
    typeSpeed: 80,
    deleteSpeed: 50,
    delayBetweenPhrases: 1500,
    loop: true,
    cursorGlow: true  // New option for cursor glow effect
  };
  
  const config = { ...defaultOptions, ...options };
  let phraseIndex = 0;
  let charIndex = 0;
  let isDeleting = false;
  let typeTimeout;
  
  // Create cursor element if cursorGlow is enabled
  let cursorElement = null;
  if (config.cursorGlow) {
    cursorElement = document.createElement('span');
    cursorElement.className = 'typewriter-cursor';
    cursorElement.innerHTML = '|';
    element.parentNode.insertBefore(cursorElement, element.nextSibling);
    
    // Add CSS for glowing cursor
    const style = document.createElement('style');
    style.textContent = `
      .typewriter-cursor {
        display: inline-block;
        margin-left: 2px;
        font-weight: bold;
        color: var(--orange-yellow-crayola);
        animation: cursorBlink 1s infinite, cursorGlow 2s infinite alternate;
      }
      
      @keyframes cursorBlink {
        0%, 100% { opacity: 1; }
        50% { opacity: 0; }
      }
      
      @keyframes cursorGlow {
        0% { text-shadow: 0 0 2px rgba(255, 215, 108, 0.5); }
        100% { text-shadow: 0 0 8px rgba(255, 215, 108, 0.8), 0 0 12px rgba(255, 215, 108, 0.5); }
      }
    `;
    document.head.appendChild(style);
  }
  
  function type() {
    const currentPhrase = phrases[phraseIndex];
    
    if (isDeleting) {
      element.textContent = currentPhrase.substring(0, charIndex - 1);
      charIndex--;
    } else {
      element.textContent = currentPhrase.substring(0, charIndex + 1);
      charIndex++;
    }
    
    // Speed factors
    let typeSpeed = isDeleting ? config.deleteSpeed : config.typeSpeed;
    
    // Handle complete typing or deletion
    if (!isDeleting && charIndex === currentPhrase.length) {
      typeSpeed = config.delayBetweenPhrases;
      isDeleting = true;
    } else if (isDeleting && charIndex === 0) {
      isDeleting = false;
      phraseIndex = (phraseIndex + 1) % phrases.length;
    }
    
    typeTimeout = setTimeout(type, typeSpeed);
  }
  
  // Start the effect
  type();
  
  // Return cleanup function
  return () => {
    if (typeTimeout) clearTimeout(typeTimeout);
    if (cursorElement) cursorElement.remove();
  };
}

// Animate particles/dots in the background
export function initParticleBackground(canvas) {
  if (!canvas) return;
  
  const ctx = canvas.getContext('2d');
  let particles = [];
  let animationFrame;
  
  // Resize canvas to parent element size
  function resizeCanvas() {
    const parent = canvas.parentElement;
    canvas.width = parent.offsetWidth;
    canvas.height = parent.offsetHeight;
    
    // Regenerate particles when canvas resizes
    initParticles();
  }
  
  // Create particles with more variety
  function initParticles() {
    particles = [];
    const particleCount = Math.floor((canvas.width * canvas.height) / 10000);
    
    // Create tech-themed particles (small icons and code symbols)
    for (let i = 0; i < particleCount; i++) {
      // Randomly choose between normal particles and special tech particles
      const isSpecialParticle = Math.random() < 0.15; // 15% chance for special particles
      
      let particle = {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 1,
        vx: Math.random() * 0.5 - 0.25,
        vy: Math.random() * 0.5 - 0.25,
        color: `rgba(255, 215, 108, ${Math.random() * 0.5 + 0.1})`,
        type: 'normal'
      };
      
      // Create special particles occasionally
      if (isSpecialParticle) {
        const particleTypes = ['code', 'spring', 'vue', 'flutter', 'angular'];
        const randomType = particleTypes[Math.floor(Math.random() * particleTypes.length)];
        
        particle.type = randomType;
        particle.radius = Math.random() * 3 + 3;
        particle.alpha = Math.random() * 0.5 + 0.5;
        particle.rotation = Math.random() * 360;
        particle.rotationSpeed = (Math.random() - 0.5) * 2;
      }
      
      particles.push(particle);
    }
  }
  
  // Draw and update particles
  function drawParticles() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    particles.forEach(particle => {
      // Update position
      particle.x += particle.vx;
      particle.y += particle.vy;
      
      // Bounce off edges
      if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
      if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;
      
      // Update rotation for special particles
      if (particle.type !== 'normal' && particle.rotationSpeed) {
        particle.rotation += particle.rotationSpeed;
      }
      
      // Draw based on particle type
      if (particle.type === 'normal') {
        // Regular particle - draw circle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();
      } else {
        // Save context for rotation
        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate((particle.rotation * Math.PI) / 180);
        
        // Draw special tech-themed particles
        switch (particle.type) {
          case 'code':
            drawCodeSymbol(ctx, particle);
            break;
          case 'spring':
            drawSpringSymbol(ctx, particle);
            break;
          case 'vue':
            drawVueSymbol(ctx, particle);
            break;
          case 'flutter':
            drawFlutterSymbol(ctx, particle);
            break;
          case 'angular':
            drawAngularSymbol(ctx, particle);
            break;
        }
        
        ctx.restore();
      }
    });
    
    // Draw connecting lines between nearby particles
    drawConnections();
    
    animationFrame = requestAnimationFrame(drawParticles);
  }
  
  // Draw code symbols (like brackets, braces, etc.)
  function drawCodeSymbol(ctx, particle) {
    const symbols = ['{', '}', '[', ']', '<', '>', '(', ')', ';', '//'];
    const symbol = symbols[Math.floor(particle.x * symbols.length) % symbols.length];
    
    ctx.font = `${particle.radius * 2}px monospace`;
    ctx.fillStyle = `rgba(255, 215, 108, ${particle.alpha})`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(symbol, 0, 0);
  }
  
  // Draw Spring Boot inspired symbol
  function drawSpringSymbol(ctx, particle) {
    ctx.fillStyle = `rgba(109, 179, 63, ${particle.alpha})`;
    
    // Draw simplified leaf shape
    ctx.beginPath();
    ctx.arc(0, -particle.radius/2, particle.radius, 0, Math.PI, false);
    ctx.lineTo(particle.radius, particle.radius/2);
    ctx.lineTo(-particle.radius, particle.radius/2);
    ctx.closePath();
    ctx.fill();
  }
  
  // Draw Vue inspired symbol
  function drawVueSymbol(ctx, particle) {
    ctx.fillStyle = `rgba(65, 184, 131, ${particle.alpha})`;
    
    // Draw simplified Vue logo
    ctx.beginPath();
    ctx.moveTo(0, -particle.radius);
    ctx.lineTo(particle.radius, particle.radius);
    ctx.lineTo(-particle.radius, particle.radius);
    ctx.closePath();
    ctx.fill();
  }
  
  // Draw Flutter inspired symbol
  function drawFlutterSymbol(ctx, particle) {
    ctx.fillStyle = `rgba(69, 196, 255, ${particle.alpha})`;
    
    // Draw simplified Flutter logo (diamond shape)
    ctx.beginPath();
    ctx.moveTo(0, -particle.radius);
    ctx.lineTo(particle.radius, 0);
    ctx.lineTo(0, particle.radius);
    ctx.lineTo(-particle.radius, 0);
    ctx.closePath();
    ctx.fill();
  }
  
  // Draw Angular inspired symbol
  function drawAngularSymbol(ctx, particle) {
    ctx.fillStyle = `rgba(221, 0, 49, ${particle.alpha})`;
    
    // Draw simplified Angular logo (shield shape)
    ctx.beginPath();
    ctx.moveTo(0, -particle.radius);
    ctx.lineTo(particle.radius, particle.radius/2);
    ctx.lineTo(0, particle.radius);
    ctx.lineTo(-particle.radius, particle.radius/2);
    ctx.closePath();
    ctx.fill();
  }
  
  // Draw lines between particles that are close to each other
  function drawConnections() {
    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x;
        const dy = particles[i].y - particles[j].y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 70) {
          ctx.beginPath();
          ctx.strokeStyle = `rgba(255, 215, 108, ${0.1 * (1 - distance / 70)})`;
          ctx.lineWidth = 0.5;
          ctx.moveTo(particles[i].x, particles[i].y);
          ctx.lineTo(particles[j].x, particles[j].y);
          ctx.stroke();
        }
      }
    }
  }
  
  // Initialize canvas and animation
  resizeCanvas();
  window.addEventListener('resize', resizeCanvas);
  drawParticles();
  
  // Return cleanup function
  return () => {
    window.removeEventListener('resize', resizeCanvas);
    if (animationFrame) cancelAnimationFrame(animationFrame);
  };
}

// Track mouse movements for interactive effects
export function initMouseTracker(element, callback) {
  if (!element) return;
  
  function handleMouseMove(e) {
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const percentX = x / rect.width;
    const percentY = y / rect.height;
    
    callback(percentX, percentY);
  }
  
  element.addEventListener('mousemove', handleMouseMove);
  
  // Return cleanup function
  return () => {
    element.removeEventListener('mousemove', handleMouseMove);
  };
}

// Create interactive tech stack floating icons
export function initInteractiveTechIcons(container) {
  if (!container) return;

  // Find all tech icon elements
  const techIcons = container.querySelectorAll('.tech-icon');
  let isDragging = false;
  let draggedIcon = null;
  let offsetX = 0;
  let offsetY = 0;
  let iconPositions = [];
  
  // Function to safely get icon positions
  function calculateIconPositions() {
    iconPositions = [];
    techIcons.forEach((icon) => {
      try {
        const rect = icon.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        
        // Only calculate if we have valid measurements
        if (rect.width && rect.height && containerRect.width && containerRect.height) {
          iconPositions.push({
            x: (rect.left - containerRect.left) + rect.width / 2,
            y: (rect.top - containerRect.top) + rect.height / 2,
            initialX: (rect.left - containerRect.left) + rect.width / 2,
            initialY: (rect.top - containerRect.top) + rect.height / 2,
            rotation: 0,
            scale: 1
          });
        } else {
          // Fallback position if measurements aren't available yet
          iconPositions.push({
            x: 0,
            y: 0,
            initialX: 0,
            initialY: 0,
            rotation: 0,
            scale: 1
          });
        }
      } catch (err) {
        console.error('Error calculating icon positions:', err);
        // Add default position in case of error
        iconPositions.push({
          x: 0, y: 0, initialX: 0, initialY: 0, rotation: 0, scale: 1
        });
      }
    });
  }
  
  // Calculate initial positions after a brief delay to ensure layout is complete
  setTimeout(() => {
    calculateIconPositions();
  }, 100);
  
  // Store initial positions and set up event listeners
  techIcons.forEach((icon, index) => {
    // Create properly bound event handlers that we can remove later
    const boundStartDrag = startDrag.bind(null, index);
    const boundTouchStart = handleTouchStart.bind(null, index);
    
    // Store handlers on the icon for cleanup
    icon._dragHandlers = {
      startDrag: boundStartDrag,
      touchStart: boundTouchStart
    };
    
    // Add event listeners
    icon.addEventListener('mousedown', boundStartDrag);
    icon.addEventListener('touchstart', boundTouchStart, { passive: false });
    
    // Store original transform for later restoration
    icon.dataset.originalTransform = icon.style.transform || '';
    
    // Add hover effect for desktop
    icon.addEventListener('mouseenter', () => {
      // Only apply hover transform if not being dragged
      if (icon !== draggedIcon) {
        icon.style.transform = 'scale(1.15)';
      }
      
      // Always show label on hover
      const label = icon.querySelector('.tech-label');
      if (label) {
        // Remove any existing animation to ensure visibility
        label.style.animation = '';
        label.style.opacity = '1';
      }
    });
    
    icon.addEventListener('mouseleave', () => {
      if (icon !== draggedIcon) {
        // Restore original transform or reset to default
        icon.style.transform = icon.dataset.originalTransform || 'scale(1)';
        
        // Hide label when not hovering
        const label = icon.querySelector('.tech-label');
        if (label && !isDragging) {
          label.style.opacity = '0';
          // Restore animation if not dragging
          label.style.animation = `fadeInOut 3s infinite ${index * 0.5}s`;
        }
      }
    });
    
    // Add tech name animation with a slight delay between each icon
    const label = icon.querySelector('.tech-label');
    if (label) {
      // Use CSS variable to store the animation state
      icon.style.setProperty('--animation-delay', `${index * 0.5}s`);
      label.style.animation = `fadeInOut 3s infinite var(--animation-delay)`;
    }
  });
  
  // Event handlers for dragging
  function startDrag(index, e) {
    e.preventDefault();
    isDragging = true;
    draggedIcon = techIcons[index];
    
    const rect = draggedIcon.getBoundingClientRect();
    offsetX = e.clientX - rect.left;
    offsetY = e.clientY - rect.top;
    
    // Add event listeners for dragging
    document.addEventListener('mousemove', drag);
    document.addEventListener('mouseup', endDrag);
    
    // Apply active class for visual feedback
    draggedIcon.classList.add('dragging');
  }
  
  function handleTouchStart(index, e) {
    e.preventDefault();
    isDragging = true;
    draggedIcon = techIcons[index];
    
    const rect = draggedIcon.getBoundingClientRect();
    const touch = e.touches[0];
    offsetX = touch.clientX - rect.left;
    offsetY = touch.clientY - rect.top;
    
    // Add event listeners for touch dragging
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', endDrag);
    
    // Apply active class for visual feedback
    draggedIcon.classList.add('dragging');
  }
  
  function handleTouchMove(e) {
    e.preventDefault();
    if (!isDragging) return;
    
    const touch = e.touches[0];
    drag({ clientX: touch.clientX, clientY: touch.clientY });
  }
  
  function drag(e) {
    if (!isDragging) return;
    
    const containerRect = container.getBoundingClientRect();
    const iconRect = draggedIcon.getBoundingClientRect();
    
    // Calculate new position relative to container
    const newX = e.clientX - containerRect.left - offsetX;
    const newY = e.clientY - containerRect.top - offsetY;
    
    // Keep within bounds of container
    const boundedX = Math.max(0, Math.min(containerRect.width - iconRect.width, newX));
    const boundedY = Math.max(0, Math.min(containerRect.height - iconRect.height, newY));
    
    // Update position
    draggedIcon.style.position = 'absolute';
    draggedIcon.style.left = `${boundedX}px`;
    draggedIcon.style.top = `${boundedY}px`;
    draggedIcon.style.zIndex = '10';
    
    // Update stored position
    const index = Array.from(techIcons).indexOf(draggedIcon);
    if (index !== -1) {
      iconPositions[index].x = boundedX + iconRect.width / 2;
      iconPositions[index].y = boundedY + iconRect.height / 2;
    }
  }
  
  function endDrag() {
    if (!isDragging) return;
    
    // Remove dragging state
    isDragging = false;
    draggedIcon.classList.remove('dragging');
    draggedIcon.style.zIndex = '';
    
    // Remove event listeners
    document.removeEventListener('mousemove', drag);
    document.removeEventListener('mouseup', endDrag);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', endDrag);
    
    // Start animation to move back to original position
    const index = Array.from(techIcons).indexOf(draggedIcon);
    if (index !== -1) {
      animateBackToPosition(index);
    }
    
    draggedIcon = null;
  }
  
  // Animated return to original position
  function animateBackToPosition(index) {
    const icon = techIcons[index];
    const position = iconPositions[index];
    const rect = icon.getBoundingClientRect();
    
    // Calculate target position
    const targetX = position.initialX - rect.width / 2;
    const targetY = position.initialY - rect.height / 2;
    
    // Animate back to original position with elastic effect
    icon.style.transition = 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    icon.style.left = `${targetX}px`;
    icon.style.top = `${targetY}px`;
    
    // Reset after animation completes
    setTimeout(() => {
      // Remove positioning styles
      icon.style.transition = '';
      icon.style.position = '';
      icon.style.left = '';
      icon.style.top = '';
      icon.style.zIndex = '';
      
      // Restore original class-based animations and styles
      icon.classList.remove('dragging');
      
      // Reset position in our tracking
      iconPositions[index].x = position.initialX;
      iconPositions[index].y = position.initialY;
      
      // Restore transform from data attribute if it exists
      if (icon.dataset.originalTransform && icon.dataset.originalTransform !== '') {
        icon.style.transform = icon.dataset.originalTransform;
      }
    }, 800);
  }
  
  // Add styles for dragging
  const style = document.createElement('style');
  style.textContent = `
    .tech-icon {
      cursor: grab;
      user-select: none;
      touch-action: none;
    }
    
    .tech-icon.dragging {
      cursor: grabbing;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
    }
    
    @keyframes fadeInOut {
      0%, 100% { opacity: 0; }
      50% { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
  
  // Return a more robust cleanup function
  return () => {
    // Clean up any ongoing drag operation
    if (isDragging && draggedIcon) {
      draggedIcon.classList.remove('dragging');
      draggedIcon.style.transition = '';
      draggedIcon.style.position = '';
      draggedIcon.style.left = '';
      draggedIcon.style.top = '';
      draggedIcon.style.zIndex = '';
      isDragging = false;
      draggedIcon = null;
    }
    
    // Remove event listeners from icons using stored handlers
    techIcons.forEach((icon) => {
      if (icon._dragHandlers) {
        icon.removeEventListener('mousedown', icon._dragHandlers.startDrag);
        icon.removeEventListener('touchstart', icon._dragHandlers.touchStart);
        
        // Clean up hover styles
        icon.style.transform = '';
        const label = icon.querySelector('.tech-label');
        if (label) {
          label.style.animation = '';
          label.style.opacity = '';
        }
        
        // Remove any custom properties we added
        delete icon._dragHandlers;
        delete icon.dataset.originalTransform;
      }
    });
    
    // Remove document-level event listeners
    document.removeEventListener('mousemove', drag);
    document.removeEventListener('mouseup', endDrag);
    document.removeEventListener('touchmove', handleTouchMove);
    document.removeEventListener('touchend', endDrag);
    
    // Remove the style element
    if (style && style.parentNode) {
      style.remove();
    }
  };
}
